import {useMediaQuery} from '@mui/material'
import React, {FC, useCallback, useContext, useEffect, useRef, useState} from 'react'
import {useDispatch} from 'react-redux'
import {
  addLoadingMessage,
  deleteLoadingMessage,
  MessageChatRequestReplyType,
  messageTypes,
  onSuccessSendMessageHandler,
  SendMessageRequest,
  setIsUpdateAccount,
  useMessagesChatRequest,
  virtualGiftsOpenModal
} from 'entities/messages'
import {setIsOpenDatingSafetyPolicy} from 'features/datingSafetyPolicyModal/model'
import {SendStickersAndEmoji} from 'features/sendStickersAndEmoji'
import {VirtualGiftsModal} from 'features/virtualGiftsModal'
import {WarningModalContext, WarningModalContextType} from 'features/warningModal/context'
import {ErrorResponsesType} from 'shared/types'
import {ArrowRight, Cross, Gift, Keyboard, PaperClip, PaperPlane, Photo, Smile} from 'shared/ui/icons'
import {Menu} from 'shared/ui/menu'
import {ChatPrice} from '../chatPrice'
import {ChatSendMedia} from '../chatSendMedia'
import {SendStickersAndEmojiMobile} from '../sendStickersAndEmojiMobile'
import {AddMedia} from './icon/icons'
import styles from './styles.module.scss'

const MAX_MESSAGE_LENGTH = 300

type ChatInputProps = {
  scrollBottom: () => void
  userId: string
}

export const ChatInput: FC<ChatInputProps> = ({scrollBottom, userId}) => {
  const dispatch = useDispatch()
  const [sendMediaElement, setSendMediaElement] = useState<HTMLDivElement | null>(null)
  const inputEl = useRef<HTMLTextAreaElement | null>(null)
  const [value, setValue] = useState('')
  const [isOpenStickersMobile, setIsOpenStickersMobile] = useState(false)
  const [isAddMediaMobile, setIsAddMediaMobile] = useState<HTMLDivElement | null>(null)
  const [isRestrictedContent, setIsRestrictedContent] = useState(true)
  const is1024p = useMediaQuery(`(max-width: ${styles.is1024p})`)
  const is768p = useMediaQuery(`(max-width: ${styles.is768p})`)
  const {setWarningModal} = useContext(WarningModalContext) as WarningModalContextType
  const chatRequest = useMessagesChatRequest()

  useEffect(() => {
    setValue('')
    setIsRestrictedContent(false)

    !is768p && inputEl.current?.focus()
  }, [userId])

  const onClickLearnMore = () => {
    dispatch(setIsOpenDatingSafetyPolicy(true))
    is768p && setWarningModal({})
  }

  const checkTextareaHeight = () => {
    if (!inputEl.current) return

    inputEl.current.style.height = 'auto'
    if (inputEl.current.scrollHeight > inputEl.current.offsetHeight) {
      inputEl.current.style.height = inputEl.current.scrollHeight + 'px'
    }
  }

  const textareaChangeHandler = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setValue(event.target.value)
    checkTextareaHeight()
  }

  const addEmojiHandler = (emoji: string) => {
    setValue((value) => value + emoji)
    checkTextareaHeight()
    inputEl.current?.focus()
  }

  const sendHandler = () => {
    if (!value) return

    if (value.length > MAX_MESSAGE_LENGTH) {
      return setWarningModal({
        header: 'Something went wrong',
        text: `The provided message is too long. Please enter a message up to ${MAX_MESSAGE_LENGTH} characters long.`
      })
    }

    const id = Date.now()
    dispatch(addLoadingMessage({id, content: {text: value}}))
    setValue('')
    inputEl.current && (inputEl.current.style.height = 'auto')

    setTimeout(scrollBottom)

    let reply: MessageChatRequestReplyType | undefined

    if (chatRequest) {
      reply = {
        type: 'message',
        message: chatRequest.content.message
      }
    }

    SendMessageRequest(userId, {text: value, reply}, messageTypes.text)
      .then((data) => {
        !is768p && setIsRestrictedContent(false)

        onSuccessSendMessageHandler(data, userId, dispatch)
      })
      .catch(({error}: ErrorResponsesType) => {
        if (error === 'insufficient_funds') {
          dispatch(setIsUpdateAccount(true))
        }

        if (error === 'restricted_content') {
          if (is768p) {
            setWarningModal({
              header: 'Sending blocked',
              text: <>
                Sending contact details has been blocked.&nbsp;
                <span className={styles.learnMore} onClick={onClickLearnMore}>Learn more</span>
              </>
            })
          } else {
            setIsRestrictedContent(true)
          }
        }

        reply && dispatch(deleteLoadingMessage(id))

        setValue((prevState) => prevState === '' ? value : prevState)
        setTimeout(checkTextareaHeight)
      })
      .finally(() => !reply && dispatch(deleteLoadingMessage(id)))
  }

  const openVirtualGiftsModal = useCallback(() => dispatch(virtualGiftsOpenModal()), [dispatch, virtualGiftsOpenModal])

  const keyDownHandler = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      sendHandler()
      event.preventDefault()
    }
  }

  const openStickersMobileHandler = () => setIsOpenStickersMobile(!isOpenStickersMobile)

  return (
    <>
      <div className={styles.wrapper}>
        <div className={styles.inputContainerWrapper}>
          <div className={styles.inputContainer}>
            {!is768p && isRestrictedContent ? <div className={styles.restrictedContentWrapper}>
              <div className={styles.text}>
                Sending contact details has been blocked.
                <span className={styles.learnMore} onClick={onClickLearnMore}>Learn more</span>
              </div>
              <div className={styles.closeRestrictedContent} onClick={() => setIsRestrictedContent(false)}>
                <Cross />
              </div>
            </div> : null}
            <div className={styles.inputWrapper}>
              {
                !is1024p
                  ? <SendStickersAndEmoji {...{addEmojiHandler, userId}} />
                  : <div
                    className={styles.smileIcon}
                    onClick={openStickersMobileHandler}
                  >
                    {isOpenStickersMobile ? <Keyboard /> : <Smile />}
                  </div>
              }
              <textarea
                className={`${styles.input} ${styles.customScroll}`}
                placeholder='Message'
                ref={inputEl}
                rows={1}
                value={value}
                onChange={textareaChangeHandler}
                onKeyDown={keyDownHandler}
              />
              {
                is768p
                  ? <div onClick={(event) => {
                    const target = event.currentTarget
                    setTimeout(() => setIsAddMediaMobile(target), 100)
                  }}>
                    <div className={styles.addMedia}><AddMedia /></div>
                  </div>
                  : <>
                    <div className={styles.icon} onClick={openVirtualGiftsModal}><Gift /></div>
                    <div onClick={(event) => setSendMediaElement(event.currentTarget)}>
                      <div className={styles.icon}><PaperClip /></div>
                    </div>
                  </>
              }
              {
                value.length > MAX_MESSAGE_LENGTH
                  ? <div className={styles.messageLimit}>{MAX_MESSAGE_LENGTH - value.length}</div>
                  : null
              }
            </div>
          </div>
          <div
            className={`${styles.sendBtn} ${value ? styles.active : ''}`}
            onClick={sendHandler}
          >
            <PaperPlane />
          </div>
        </div>

        <ChatSendMedia
          userId={userId}
          sendMediaElement={sendMediaElement}
          onClose={() => setSendMediaElement(null)}
          backMedia={() => {
            setIsAddMediaMobile(sendMediaElement)
            setSendMediaElement(null)
          }}
          scrollBottom={scrollBottom}
        />

        {is768p ? <AddMediaMenu
          anchorElement={isAddMediaMobile}
          onClose={() => setIsAddMediaMobile(null)}
          openMedia={() => {
            setSendMediaElement(isAddMediaMobile)
            setIsAddMediaMobile(null)
          }}
        /> : null}

        <VirtualGiftsModal idUser={userId} />
      </div>

      {!is768p ? <ChatPrice /> : null}

      {is1024p && isOpenStickersMobile
        ? <SendStickersAndEmojiMobile
          {...{addEmojiHandler, userId}}
          onSendStickerHandler={() => setIsOpenStickersMobile(false)}
        />
        : null}
    </>
  )
}

type AddMediaMenuProps = {
  anchorElement: HTMLDivElement | null
  onClose: () => void
  openMedia: () => void
}

const AddMediaMenu: FC<AddMediaMenuProps> = ({anchorElement, onClose, openMedia}) => {
  const dispatch = useDispatch()

  const openVirtualGiftsModal = useCallback(() => {
    dispatch(virtualGiftsOpenModal())
    onClose()
  }, [dispatch, virtualGiftsOpenModal, onClose])

  return (
    <Menu
      anchorEl={anchorElement}
      open={Boolean(anchorElement)}
      onClose={onClose}
      anchorOrigin={{
        vertical: -4,
        horizontal: 46
      }}
      transformOrigin={{
        vertical: 'bottom',
        horizontal: 'right'
      }}
    >
      <div className={styles.popup}>
        <div className={styles.item} onClick={openVirtualGiftsModal}>
          <div className={styles.left}>
            <div className={styles.icon}><Gift /></div>
            Gifts
          </div>
        </div>
        <div className={styles.item} onClick={openMedia}>
          <div className={styles.left}>
            <div className={styles.icon}>
              <Photo />
            </div>
            Media
          </div>
          <div className={styles.arrowRight}>
            <ArrowRight />
          </div>
        </div>
      </div>
    </Menu>
  )
}